import React, {
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import { Input } from 'reactstrap';
import {
  getSummaryMetrics,
  // getParticipantMetrics,
  // generateBaseParticipantMetrics,
} from 'js/utils/question-set';
import { mapValues, min, max, reduce, flatMap, map, isEqual } from 'lodash';
import AssessmentTrendsChart from './AssessmentTrendsChart';
import { scaleTime } from '@visx/scale';

import styles from './AssessmentTrends.module.scss';
import { withParentSize } from '@visx/responsive';
import DetailsPane from './DetailsPane/DetailsPane';
import { useFilter } from './useFilter';
import ItemBlock from './ItemBlock';
import SYMBOLS from './symbols';
import { isNullOrWhitespace } from 'js/utils/string';

function generateKey(questionSetKey, questionKey) {
  return `${questionSetKey}-${questionKey}`;
}

function generatePaletteMap(summaryMetrics) {
  const { questionSets } = summaryMetrics;

  let i = 1;
  return reduce(
    questionSets,
    (acc, questionSet, questionSetKey) => {
      acc[generateKey(questionSetKey, null)] = {
        questionSetKey,
        questionSet,
        questionKey: null,
        question: null,
        className: `palette-chart-dark-${i++}`,
      };
      let j = 1;
      return reduce(
        questionSet.questions,
        (acc, question, questionKey) => {
          acc[generateKey(questionSetKey, questionKey)] = {
            questionSetKey,
            questionSet,
            questionKey,
            question,
            className: `palette-chart-dark-${j++}`,
          };
          return acc;
        },
        acc
      );
    },
    {}
  );
}

function isFilterForQuestion(filter, questionSetKey, questionKey) {
  return (
    (filter?.type === 'question' || filter?.subtype === 'question') &&
    filter?.criteria?.questionSetKey === questionSetKey &&
    filter?.criteria?.questionKey === questionKey
  );
}

// function generateAssessmentFilter(assessmentId) {
//   return { type: 'assessment', criteria: { assessmentId } };
// }
function generateQuestionFilter(questionSetKey, questionKey) {
  return { type: 'question', criteria: { questionSetKey, questionKey } };
}

function generateOverviewQuestionFilter(questionSetKey, questionKey) {
  return {
    type: 'overview',
    subtype: 'question',
    criteria: { questionSetKey, questionKey },
  };
}

function filterHandler(filterLevel, filterGenerator, filterSetter) {
  return (...args) => {
    filterSetter(filterLevel, filterGenerator(...args));
  };
}
const filterLayers = ['transient', 'locked'];

export const AssessmentTrends = withParentSize(
  ({ assessments, parentWidth }) => {
    const [chartWidth, setChartWidth] = useState(0);
    const gridElemRef = useRef(null);
    const chartElemRef = useRef(null);
    const { setFilter, clearFilter, toggleFilter, activeFilter, filterStack } =
      useFilter(filterLayers, isEqual);

    const [filterQuestionSet, setFilterQuestionSet] = useState(null);

    // const lollipopFilters = [...filterLayers].reverse().reduce(
    //   (acc, layer) => {
    //     const filter = filterStack[layer];
    //     if (filter?.type === 'question') {
    //       return {
    //         ...acc,
    //         question: filter,
    //       };
    //     } else if (filter?.type === 'assessment') {
    //       if (layer === 'locked') {
    //         return {
    //           ...acc,
    //           assessmentLocked: filter,
    //         };
    //       } else if (layer === 'transient') {
    //         return {
    //           ...acc,
    //           assessmentTransient: filter,
    //         };
    //       }
    //     }

    //     return acc;
    //   },
    //   {
    //     question: null,
    //     assessmentLocked: null,
    //     assessmentTransient: null,
    //   }
    // );

    const handleSetTransientQuestionFilter = useMemo(
      () => filterHandler('transient', generateQuestionFilter, setFilter),
      [setFilter]
    );

    const handleToggleLockedQuestionFilter = useMemo(
      () => filterHandler('locked', generateQuestionFilter, toggleFilter),
      [toggleFilter]
    );

    // const handleSetTransientAssessmentFilter = useMemo(
    //   () => filterHandler('transient', generateAssessmentFilter, setFilter),
    //   [setFilter]
    // );

    // const handleToggleLockedAssessmentFilter = useMemo(
    //   () => filterHandler('locked', generateAssessmentFilter, toggleFilter),
    //   [toggleFilter]
    // );

    const handleSliceOver = useMemo(
      () =>
        filterHandler('transient', generateOverviewQuestionFilter, setFilter),
      [setFilter]
    );

    const clearTransientFilter = () => {
      clearFilter('transient');
    };

    useLayoutEffect(() => {
      const { width } = chartElemRef.current?.getBoundingClientRect() ?? {
        width: 0,
      };

      setChartWidth(width);
    }, [parentWidth]);

    const changeQuestionSetFilter = useCallback(
      (questionSetKey) => {
        const newValue = isNullOrWhitespace(questionSetKey)
          ? null
          : questionSetKey;
        if (newValue !== filterQuestionSet) {
          setFilterQuestionSet(newValue);
          clearFilter('locked', 'transient');
        }
      },
      [setFilterQuestionSet, clearFilter, filterQuestionSet]
    );

    const handleQuestionSetChange = useCallback(
      (e) => changeQuestionSetFilter(e.target.value),
      [changeQuestionSetFilter]
    );

    const handleSliceClick = useCallback(
      (questionSetKey) => changeQuestionSetFilter(questionSetKey),
      [changeQuestionSetFilter]
    );

    const chartMargins = { top: 10, right: 10, bottom: 15, left: 10 };

    const {
      dataPoints,
      summaryData,
      maxDate,
      minDate,
      paletteMap,
      lollipopData,
      className,
    } = useMemo(() => {
      // const assessmentLookup = keyBy(assessments, 'id');
      const summaryData = getSummaryMetrics(assessments);
      // const participantMetrics = getParticipantMetrics(assessments);
      const paletteMap = generatePaletteMap(summaryData);

      const assessmentSequence = [...assessments]
        .sort((a, b) => a.inserted_at - b.inserted_at)
        .map((a) => ({ id: a.id, insertedAt: new Date(a.inserted_at) }));

      const dataPoints = mapValues(
        summaryData.questionSets,
        (questionSet, qsKey) => {
          const questionData = mapValues(
            questionSet.questions,
            (question, qKey) => {
              return {
                text: question.text,
                data: assessmentSequence
                  .map(({ id, insertedAt }) => {
                    const questionData =
                      summaryData.assessments[id].questionSets[qsKey]
                        ?.questions[qKey];
                    if (!questionData) {
                      return null;
                    }

                    const { normalizedScore } = questionData;
                    return { insertedAt, normalizedScore };
                  })
                  .filter((x) => x?.normalizedScore != null),
              };
            }
          );

          return {
            ...questionData,
            [SYMBOLS.DATA]: assessmentSequence
              .map(({ id, insertedAt }) => {
                const questionSetData =
                  summaryData.assessments[id].questionSets[qsKey];

                const questionData = questionSetData
                  ? Object.values(questionSetData.questions).reduce(
                      (acc, q) => {
                        return q.normalizedScore == null
                          ? acc
                          : {
                              min:
                                acc.min == null
                                  ? q.normalizedScore
                                  : Math.min(acc.min, q.normalizedScore),
                              max:
                                acc.max == null
                                  ? q.normalizedScore
                                  : Math.max(acc.max, q.normalizedScore),
                            };
                      },
                      { min: null, max: null }
                    )
                  : { min: null, max: null };

                return {
                  insertedAt,
                  ...questionData,
                  normalizedScore: questionSetData?.normalizedScore,
                };
              })
              .filter((x) => x?.normalizedScore != null),
          };
        }
      );

      // const lollipopData = assessmentSequence.map(({ id }) => {
      //   const rawAssessment = assessmentLookup[id];

      //   const baseParticipantMetrics = rawAssessment.participants.reduce(
      //     (acc, { id }) => ({
      //       ...acc,
      //       [id]: generateBaseParticipantMetrics(),
      //     }),
      //     {}
      //   );

      //   return {
      //     id,
      //     ...summaryData.assessments[id],
      //     participantMetrics: {
      //       ...baseParticipantMetrics,
      //       ...participantMetrics[id],
      //     },
      //     rawAssessment,
      //   };
      // });

      const minDayRange = 30;
      let minDate = min(assessmentSequence.map(({ insertedAt }) => insertedAt));
      let maxDate = max(assessmentSequence.map(({ insertedAt }) => insertedAt));
      if (!!minDate) {
        const dateRangeDays = (maxDate - minDate) / (1000 * 60 * 60 * 24);

        if (dateRangeDays < minDayRange) {
          const dateRangePadding =
            (minDayRange - dateRangeDays) * 1000 * 60 * 60 * 24;
          minDate = new Date(minDate.getTime() - 0.8 * dateRangePadding);
          maxDate = new Date(maxDate.getTime() + 0.2 * dateRangePadding);
        } else {
          const dateRangePadding = dateRangeDays * 1000 * 60 * 60 * 24 * 0.03;

          minDate = new Date(minDate.getTime() - dateRangePadding);
          maxDate = new Date(maxDate.getTime() + dateRangePadding);
        }
      }

      return {
        minDate,
        maxDate,
        assessmentSequence,
        dataPoints,
        summaryData,
        paletteMap,
        lollipopData: [],
      };
    }, [assessments]);

    const questionSet = useMemo(() => {
      const questionSetKeys = Object.keys(dataPoints);
      return questionSetKeys.length === 1
        ? questionSetKeys[0]
        : filterQuestionSet;
    }, [dataPoints, filterQuestionSet]);

    const xScale = scaleTime({
      domain: [minDate, maxDate],
      range: [0, chartWidth - chartMargins.left - chartMargins.right],
    });

    return (
      <div className={className}>
        <div>
          <div className={styles['chart-grid']} ref={gridElemRef}>
            <div className={styles.deets}>
              <DetailsPane
                filterStack={filterStack}
                filter={activeFilter}
                questionSetKey={questionSet}
                assessments={assessments}
                summaryData={summaryData}
                lollipopData={lollipopData}
                onSliceOver={handleSliceOver}
                onSliceClick={handleSliceClick}
                onSliceOut={clearTransientFilter}
                paletteMap={paletteMap}
              />
            </div>
            {/* <div className={styles.lollipop} ref={chartElemRef}>
              <label>Scores</label>
              <AssessmentLollipopChart
                questionFilter={lollipopFilters.question}
                transientAssessmentFilter={lollipopFilters.assessmentTransient}
                lockedAssessmentFilter={lollipopFilters.assessmentLocked}
                assessments={lollipopData}
                width={chartWidth}
                height={200}
                margins={{ ...chartMargins, bottom: 15 }}
                xScale={xScale}
                onHoverAssessment={handleSetTransientAssessmentFilter}
                onUnhoverAssessment={clearTransientFilter}
                onClickAssessment={handleToggleLockedAssessmentFilter}
              />
            </div> */}
            <div className={styles.lines} ref={chartElemRef}>
              <div className={styles['lines-header']}>
                <div>
                  <label>Trends</label>
                </div>
                <div>
                  <Input
                    type="select"
                    onChange={handleQuestionSetChange}
                    className="dark"
                    value={filterQuestionSet ?? ''}
                  >
                    <option value="">Overview</option>
                    {Object.keys(summaryData.questionSets).map(
                      (questionSetKey) => {
                        return (
                          <option value={questionSetKey} key={questionSetKey}>
                            {
                              summaryData.questionSets[questionSetKey]
                                .displayName
                            }
                          </option>
                        );
                      }
                    )}
                  </Input>
                </div>
              </div>
              <AssessmentTrendsChart
                width={chartWidth}
                height={425}
                margins={chartMargins}
                assessments={summaryData.assessments}
                dataPoints={dataPoints}
                questionSet={questionSet}
                paletteMap={paletteMap}
                xScale={xScale}
                filter={activeFilter}
                onHoverTrend={handleSetTransientQuestionFilter}
                onUnhoverTrend={clearTransientFilter}
                onClickTrend={handleToggleLockedQuestionFilter}
                generateKey={generateKey}
              />
            </div>
          </div>
        </div>
        <div className={`${styles['question-selectors']}`}>
          <ol className="">
            {questionSet != null
              ? flatMap(
                  summaryData.questionSets,
                  (questionSet, questionSetKey) =>
                    filterQuestionSet !== questionSetKey
                      ? null
                      : map(questionSet.questions, (question, questionKey) => {
                          const key = generateKey(questionSetKey, questionKey);
                          const paletteClassName = paletteMap[key]?.className;
                          const [isLockedStyle, isTransientStyle] = [
                            filterStack.locked,
                            filterStack.transient,
                          ].map((stack) =>
                            isFilterForQuestion(
                              stack,
                              questionSetKey,
                              questionKey
                            )
                          );

                          return (
                            <ItemBlock
                              key={key}
                              paletteClassName={paletteClassName}
                              isLockedStyle={isLockedStyle}
                              isTransientStyle={isTransientStyle}
                              onMouseEnter={handleSetTransientQuestionFilter}
                              onClick={handleToggleLockedQuestionFilter}
                              clearTransientFilter={clearTransientFilter}
                              questionSetKey={questionSetKey}
                              questionKey={questionKey}
                            >
                              {question.text}
                            </ItemBlock>
                          );
                        })
                )
              : map(summaryData.questionSets, (questionSet, questionSetKey) => {
                  const key = generateKey(questionSetKey, null);
                  const paletteClassName = paletteMap[key]?.className;
                  const [isLockedStyle, isTransientStyle] = [
                    filterStack.locked,
                    filterStack.transient,
                  ].map((stack) =>
                    isFilterForQuestion(stack, questionSetKey, null)
                  );

                  return (
                    <ItemBlock
                      key={key}
                      paletteClassName={paletteClassName}
                      isLockedStyle={isLockedStyle}
                      isTransientStyle={isTransientStyle}
                      onMouseEnter={handleSetTransientQuestionFilter}
                      onClick={handleToggleLockedQuestionFilter}
                      clearTransientFilter={clearTransientFilter}
                      questionSetKey={questionSetKey}
                      questionKey={null}
                    >
                      {questionSet.displayName}
                    </ItemBlock>
                  );
                })}
          </ol>
        </div>
      </div>
    );
  }
);

export default AssessmentTrends;
